import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { ChartWheel } from './ChartWheel';
import { BigThreeCard } from './BigThreeCard';
import { AspectGrid } from './AspectGrid';
import { PlanetLegend } from './PlanetLegend';
import { ChartPatterns } from './ChartPatterns';
import { PlanetTable } from './PlanetTable';
import { AspectList } from './AspectList';
import { Chart } from '../engine/chart';
import { HOUSE_SYSTEMS } from '../engine/houses';

interface Props {
  chart: Chart;
  /** Optional extra trigger nodes appended to the tab list (e.g. Transits, Synastry, AI). */
  extraTriggers?: React.ReactNode;
  /** Optional extra TabsContent panels rendered after the read-only ones. */
  extraContent?: React.ReactNode;
  defaultValue?: string;
}

function houseSystemLabel(chart: Chart): string {
  if (chart.unknownTime) return 'Houses unavailable';
  const sys = HOUSE_SYSTEMS.find((s) => s.value === chart.houseSystem)?.label ?? 'Placidus';
  return `${sys} houses`;
}

export const ChartReadOnlyTabs: React.FC<Props> = ({
  chart,
  extraTriggers,
  extraContent,
  defaultValue = 'chart',
}) => {
  return (
    <Tabs defaultValue={defaultValue}>
      <TabsList className="flex flex-wrap h-auto">
        <TabsTrigger value="chart">Chart</TabsTrigger>
        <TabsTrigger value="bigthree">Big Three</TabsTrigger>
        <TabsTrigger value="patterns">Patterns</TabsTrigger>
        <TabsTrigger value="placements">Placements</TabsTrigger>
        <TabsTrigger value="aspects">Aspects</TabsTrigger>
        <TabsTrigger value="grid">Grid</TabsTrigger>
        {extraTriggers}
      </TabsList>

      <TabsContent value="chart" className="pt-4">
        <Card className="p-4 bg-card/50">
          <p className="text-xs text-muted-foreground text-center mb-2">{houseSystemLabel(chart)}</p>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-start">
            <div className="flex justify-center">
              <ChartWheel chart={chart} size={520} />
            </div>
            <PlanetLegend chart={chart} className="w-full md:w-56" />
          </div>
        </Card>
      </TabsContent>

      <TabsContent value="bigthree" className="pt-4">
        <BigThreeCard chart={chart} />
      </TabsContent>

      <TabsContent value="patterns" className="pt-4">
        <ChartPatterns chart={chart} />
      </TabsContent>

      <TabsContent value="placements" className="pt-4">
        <p className="text-xs text-muted-foreground mb-2">{houseSystemLabel(chart)}</p>
        <PlanetTable chart={chart} />
      </TabsContent>

      <TabsContent value="aspects" className="pt-4">
        <AspectList chart={chart} />
      </TabsContent>

      <TabsContent value="grid" className="pt-4">
        <AspectGrid chart={chart} />
      </TabsContent>

      {extraContent}
    </Tabs>
  );
};